import { useCallback, useState } from 'react';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';

import { auth } from '../services/firebase';
import { subscriptionService } from '../services/subscriptionService';
import { saveUserProfile, updateUserLastLogin } from '../services/userService';
import type { AuthView } from '../types';

type UseFirebaseAuthActionsParams = {
  t: (key: string) => string;
  validatePassword: (password: string) => boolean;
  setLoading: (v: boolean) => void;
  setStatusMessage: (msg: { type: 'success' | 'error'; text: string } | null) => void;
  isOAuthAuthenticated: boolean;
  oauthLogout: () => void;
  currentUser: FirebaseUser | null;
};

export const useFirebaseAuthActions = ({
  t,
  validatePassword,
  setLoading,
  setStatusMessage,
  isOAuthAuthenticated,
  oauthLogout,
  currentUser
}: UseFirebaseAuthActionsParams) => {
  const [authView, setAuthView] = useState<AuthView>('login');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [currentPassword, setCurrentPassword] = useState<string>('');

  const handleLogout = useCallback(async () => {
    try {
      setCurrentPassword('');
      setPasswordError(null);
      setAuthView('login');

      if (currentUser) {
        await signOut(auth);
      }
      if (isOAuthAuthenticated) {
        oauthLogout();
      }
    } catch (error) {
      console.error('Logout error:', error);
      setStatusMessage({ type: 'error', text: 'Erreur déconnexion' });
    }
  }, [currentUser, isOAuthAuthenticated, oauthLogout, setStatusMessage]);

  const handleLogin = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const formData = new FormData(e.currentTarget);

    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        formData.get('email') as string,
        formData.get('password') as string
      );
      await updateUserLastLogin(userCredential.user);
    } catch (error: any) {
      let errorMessage = 'Erreur lors de la connexion';
      
      // Gérer les erreurs Firebase spécifiques
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'Aucun compte trouvé avec cet email. Vérifiez l\'email ou créez un compte.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Mot de passe incorrect. Veuillez réessayer.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'L\'adresse email n\'est pas valide.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives de connexion. Veuillez réessayer plus tard.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'Ce compte a été désactivé.';
          break;
        default:
          errorMessage = error.message || 'Erreur lors de la connexion';
      }
      
      setStatusMessage({ type: 'error', text: errorMessage });
      setLoading(false);
    }
  }, [setLoading, setStatusMessage]);

  const handleRegister = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setPasswordError(null);
    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (!validatePassword(password)) {
      setPasswordError(t('app:auth.password_requirements'));
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setPasswordError(t('app:auth.passwords_not_match'));
      setLoading(false);
      return;
    }

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.get('email') as string,
        password
      );
      await saveUserProfile(userCredential.user, formData.get('displayName') as string, '', {}, 'user');

      try {
        const existing = await subscriptionService.getUserSubscription(userCredential.user.uid);
        if (!existing) {
          await subscriptionService.createFreeSubscription(userCredential.user.uid);
        }
      } catch (err) {
        console.error('Error creating free subscription on register:', err);
      }
    } catch (error: any) {
      let errorMessage = 'Erreur lors de la création du compte';
      
      // Gérer les erreurs Firebase spécifiques
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'Un compte avec cet email existe déjà. Utilisez un autre email ou connectez-vous.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Le mot de passe est trop faible. Choisissez un mot de passe plus robuste.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'L\'adresse email n\'est pas valide.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Trop de tentatives de création. Veuillez réessayer plus tard.';
          break;
        default:
          errorMessage = error.message || 'Erreur lors de la création du compte';
      }
      
      setStatusMessage({ type: 'error', text: errorMessage });
      setLoading(false);
    }
  }, [setLoading, setStatusMessage, t, validatePassword]);

  return {
    authView,
    setAuthView,
    passwordError,
    setPasswordError,
    currentPassword,
    setCurrentPassword,
    handleLogin,
    handleRegister,
    handleLogout
  };
};
